/**
 * PagoSinIdentificarFormCxP.js — Pago a proveedor sin factura identificada.
 * Espejo de DepositoSinIdentificarForm (CxC, AddReceipt.js), pero invirtiendo
 * la dirección: aquí SALE dinero del banco. Asiento inmediato:
 *   DEBE  → Pagos sin Identificar (Activo)
 *   HABER → Banco (Activo)
 * Apunta a POST /api/finance/reciboCxP/pago-sin-identificar/. El proveedor es
 * opcional (preselección para cuando se aplique después).
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { useMultimoneda } from "../../../../shared/hooks/useMultimoneda";
import { useTipoCambio } from "../../services/useTipoCambio";
import { SearchSelect } from "../../../../shared/components/SearchSelect";
import DecimalInput from "../../../../shared/components/DecimalInput";

export default function PagoSinIdentificarFormCxP({ form, setForm, error, setError, saving, setSaving, onCreated }) {
  const { t } = useTranslation();
  const [cuentas, setCuentas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const multimoneda = useMultimoneda();
  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  useEffect(() => {
    if (tipoCambio?.tipo_compra) {
      setForm((f) => ({ ...f, tipo_cambio: Number(tipoCambio.tipo_compra) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCambio]);

  useEffect(() => {
    apiFetch("/api/finance/cuentaBancaria/")
      .then((r) => r.json())
      .then((d) => {
        const todas = d.results ?? d ?? [];
        const list = multimoneda ? todas : todas.filter((c) => (c.moneda || "CRC").toUpperCase() === "CRC");
        setCuentas(list);
        if (list.length > 0 && !form.cuenta_bancaria) {
          const first = list[0];
          setForm((f) => ({ ...f, cuenta_bancaria: String(first.id), moneda_pago: (first.moneda || "CRC").toUpperCase() }));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multimoneda]);

  useEffect(() => {
    apiFetch("/api/finance/catalogo/detalle/?tipo=PASIVO&excluir_a_favor=1&bajo_relacion=proveedores&page_size=500")
      .then((r) => r.json())
      .then((d) => setProveedores(Array.isArray(d) ? d : (d.results ?? [])))
      .catch(() => {});
  }, []);

  function handleBancoChange(e) {
    const id = e.target.value;
    const cuenta = cuentas.find((c) => String(c.id) === id);
    setForm((f) => ({ ...f, cuenta_bancaria: id, moneda_pago: cuenta ? (cuenta.moneda || "CRC").toUpperCase() : "CRC" }));
  }

  const esUSD = form.moneda_pago === "USD";
  const tcNum = Number(form.tipo_cambio) || 0;

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.monto || Number(form.monto) <= 0) { setError(t("cxp.montoReq")); return; }
    if (!form.fecha_pago) { setError(t("cxp.fechaPagoReq")); return; }
    if (!form.cuenta_bancaria) { setError(t("cxp.cuentaBancariaReq")); return; }
    if (!form.documento.trim()) { setError(t("cxp.referenciaObligatoria")); return; }
    if (multimoneda && (!form.tipo_cambio || Number(form.tipo_cambio) <= 1)) {
      setError(t("cxp.tipoCambioInvalido"));
      return;
    }
    setError(""); setSaving(true);
    try {
      const body = {
        monto:              Number(form.monto),
        fecha_pago:         form.fecha_pago,
        metodo_pago:        form.metodo_pago,
        documento:          form.documento,
        descripcion:        form.descripcion,
        cuenta_bancaria:    Number(form.cuenta_bancaria),
        moneda_pago:        form.moneda_pago,
        tipo_cambio:        Number(form.tipo_cambio) || 1,
        cuenta_proveedor:   form.cuenta_proveedor ? Number(form.cuenta_proveedor) : null,
      };
      const res = await apiFetch("/api/finance/reciboCxP/pago-sin-identificar/", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || JSON.stringify(data)); return; }
      onCreated(data);
    } catch (e) {
      setError(`${t("cxp.errorGuardar")}: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
      {/* Panel izquierdo */}
      <div style={{ flex: "1 1 340px", background: "var(--bg-card)",
        border: "1px solid var(--border-card)", borderRadius: 12, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20,
          paddingBottom: 16, borderBottom: "1px solid var(--border-card)" }}>
          <div style={{ background: "rgba(249,115,22,0.15)", borderRadius: 8, padding: "8px 10px", color: "#f97316" }}>
            <i className="fa-solid fa-vault" style={{ fontSize: "1.1rem", color: "#f97316" }} />
          </div>
          <div>
            <div style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "0.95rem" }}>
              {t("cxp.detallesPagoSinIdentificar")}
            </div>
            <div style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{t("cxp.sinFacturaIdentificada")}</div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
              {t("cxp.fechaPago")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input type="date" value={form.fecha_pago} onChange={(e) => set("fecha_pago", e.target.value)}
              style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
                border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }} />
          </div>
          <div>
            <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
              {t("cxp.monto")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input type="number" min="0.01" step="0.01" value={form.monto} placeholder="0.00"
              onChange={(e) => set("monto", e.target.value)}
              style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
                border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }} />
          </div>
          <div>
            <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
              {t("cxp.medioPago")}
            </label>
            <select value={form.metodo_pago} onChange={(e) => set("metodo_pago", e.target.value)}
              style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
                border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }}>
              <option value="TRANSFERENCIA">{t("cxp.transferencia")}</option>
              <option value="EFECTIVO">{t("cxp.efectivo")}</option>
              <option value="CHEQUE">{t("cxp.cheque")}</option>
              <option value="TARJETA">{t("cxp.tarjeta")}</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
              {t("cxp.cuentaBancaria")} <span style={{ color: "#f87171" }}>*</span>
            </label>
            <select value={form.cuenta_bancaria} onChange={handleBancoChange}
              style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
                border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }}>
              <option value="">{t("cxp.seleccionaProveedor").replace(/proveedor/i, "cuenta")}</option>
              {cuentas.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.banco} - {c.numero_cuenta}{(c.moneda || "CRC").toUpperCase() === "USD" ? " (USD)" : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            {t("cxp.proveedor")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.opcional")})</span>
          </label>
          <SearchSelect
            icon="fa-building"
            options={proveedores.map((c) => ({ id: c.id, primary: c.codigo, secondary: c.nombre }))}
            value={form.cuenta_proveedor ? Number(form.cuenta_proveedor) : null}
            onChange={(id) => set("cuenta_proveedor", id ? String(id) : "")}
            placeholder={t("cxp.seleccionaProveedor")}
          />
          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 3 }}>
            {t("cxp.proveedorSinAplicarHint")}
          </div>
        </div>

        {multimoneda && (
          <div style={{ marginBottom: 14, border: "1px solid var(--border-card)", borderRadius: 10,
            overflow: "hidden", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
              borderBottom: "1px solid var(--border-card)", background: "rgba(249,115,22,0.04)" }}>
              <i className="fa-solid fa-coins" style={{ color: "#f97316", fontSize: "0.7rem" }} />
              <span style={{ fontSize: "0.67rem", fontWeight: 700, color: "var(--text-muted)",
                letterSpacing: "0.6px", textTransform: "uppercase" }}>
                {t("cxp.tipoCambioRef")}
              </span>
            </div>
            <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: "0.73rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>TC compra:</span>
              {loadingTC
                ? <i className="fa-solid fa-circle-notch fa-spin" style={{ color: "#60a5fa", fontSize: "0.7rem" }} />
                : <span style={{ fontSize: "0.64rem", fontWeight: 700, background: "#4ade8018",
                    border: "1px solid #4ade8045", borderRadius: 4, padding: "2px 7px", color: "#4ade80" }}>BCCR</span>}
              <div style={{ display: "flex", alignItems: "center", gap: 4, background: "rgba(0,0,0,0.2)",
                border: "1px solid var(--border-card)", borderRadius: 7, padding: "4px 10px 4px 9px" }}>
                <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", fontWeight: 600 }}>₡</span>
                <DecimalInput value={form.tipo_cambio || ""}
                  onChange={(e) => set("tipo_cambio", e.target.value)}
                  style={{ width: 82, background: "transparent", border: "none", color: "var(--text-primary)",
                    fontSize: "0.88rem", fontFamily: "monospace", fontWeight: 600, outline: "none", padding: 0 }} />
                <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>/ USD</span>
              </div>
              {esUSD && tcNum > 1 && (
                <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                  ≈ ₡{(Number(form.monto || 0) * tcNum).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </span>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            {t("cxp.referenciaBancaria")} <span style={{ color: "#f87171" }}>*</span>
          </label>
          <input type="text" value={form.documento} onChange={(e) => set("documento", e.target.value)}
            placeholder={t("cxp.referenciaPlaceholder")}
            style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
              border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", color: "var(--text-secondary)", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            {t("cxp.descripcionLabel")} <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>({t("common.opcional")})</span>
          </label>
          <input type="text" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)}
            placeholder={t("cxp.descripcionPlaceholder")}
            style={{ width: "100%", padding: "8px 12px", background: "var(--bg-input)",
              border: "1px solid var(--border-card)", borderRadius: 7, color: "var(--text-primary)", fontSize: "0.88rem" }} />
        </div>

        {error && (
          <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: 7, padding: "8px 14px", color: "#f87171", fontSize: "0.84rem", marginBottom: 12 }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} />{error}
          </div>
        )}
      </div>

      {/* Panel derecho */}
      <div style={{ flex: "1 1 280px", background: "var(--bg-card-alt)",
        border: "1px solid var(--border-card)", borderRadius: 12, padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20,
          paddingBottom: 16, borderBottom: "1px solid var(--border-card)" }}>
          <div style={{ background: "rgba(99,102,241,0.15)", borderRadius: 8, padding: "8px 10px", color: "#818cf8" }}>
            <i className="fa-solid fa-circle-info" style={{ fontSize: "1.1rem", color: "#818cf8" }} />
          </div>
          <div style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: "0.95rem" }}>{t("cxp.registroContable")}</div>
        </div>

        <div style={{ background: "rgba(249,115,22,0.07)", border: "1px solid rgba(249,115,22,0.2)",
          borderRadius: 8, padding: "14px 16px", marginBottom: 20, fontSize: "0.82rem", color: "#fdba74", lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("cxp.asientoGenerado")}:</div>
          <div>DEBE → {t("cxp.pagosSinIdentificar")} (Activo)</div>
          <div>HABER → {t("cxp.bancoLabel")} (Activo)</div>
          <div style={{ marginTop: 10, color: "var(--text-secondary)", fontSize: "0.77rem" }}>
            {t("cxp.aplicarLuegoHint")}
          </div>
        </div>

        <div style={{ borderTop: "1px solid var(--border-card)", paddingTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", color: "var(--text-secondary)", fontSize: "0.82rem" }}>
            <span>{t("cxp.montoARegistrar")}</span>
            <span style={{ color: "#f97316", fontWeight: 700, fontSize: "1rem" }}>
              {esUSD
                ? `$${form.monto ? Number(form.monto).toFixed(2) : "0.00"}`
                : `₡${form.monto ? Number(form.monto).toLocaleString("en-US", { minimumFractionDigits: 2 }) : "0.00"}`}
            </span>
          </div>
          {esUSD && tcNum > 1 && (
            <div style={{ display: "flex", justifyContent: "space-between", color: "var(--text-muted)", fontSize: "0.78rem" }}>
              <span>{t("cxp.equivalenteCRC", { tc: tcNum })}</span>
              <span style={{ color: "var(--text-secondary)", fontWeight: 600 }}>
                ₡{(Number(form.monto || 0) * tcNum).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </span>
            </div>
          )}
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ width: "100%", padding: "11px", background: saving ? "rgba(249,115,22,0.5)" : "#f97316",
              border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: "0.9rem",
              cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <i className="fa-solid fa-floppy-disk" />
            {saving ? `${t("cxp.guardando")}…` : t("cxp.guardarPagoSinIdentificar")}
          </button>
        </div>
      </div>
    </div>
  );
}
